'use client';

import { Button } from '@curology/ui-components-web/react/Button';
import { CheckmarkIcon } from '@curology/ui-components-web/react/Icon/icons/CheckmarkIcon';
import { EditIcon } from '@curology/ui-components-web/react/Icon/icons/EditIcon';
import { FormulaChangeIcon } from '@curology/ui-components-web/react/Icon/icons/FormulaChangeIcon';
import { InteractiveList } from '@curology/ui-components-web/react/InteractiveList';
import { InteractiveListItem } from '@curology/ui-components-web/react/InteractiveList/InteractiveListItem';
import { Typography } from '@curology/ui-components-web/react/Typography';
import { useAtom } from 'jotai/index';
import Image from 'next/image';
import React, { useEffect, useMemo, useState } from 'react';

import { FlashMessagesState } from '@/atoms/flashMessages/flashMessagesState';
import EditSubscriptionModal from '@/components/flow/steps/resubscription-checkout/components/editSubscriptionModal';
import PaymentMethodModal from '@/components/flow/steps/resubscription-checkout/components/paymentMethodModal';
import PaymentMethodSummary from '@/components/flow/steps/resubscription-checkout/components/paymentMethodSummary';
import ProductCard from '@/components/flow/steps/resubscription-checkout/components/productCard';
import ShippingAddressModal from '@/components/flow/steps/resubscription-checkout/components/shippingAddressModal';
import SubscriptionTermsModal from '@/components/flow/steps/resubscription-checkout/components/subscriptionTermsModal';
import { createPaymentInvoice } from '@/components/payments/utils';
import { AnimatedPurchaseContextVisibility } from '@/components/product-selection/components/AnimatedPurchaseContextVisibility';
import { usePurchaseContext } from '@/components/product-selection/hooks/usePurchaseContext';
import { PurchaseContextToggle } from '@/components/purchaseContextToggle';
import { ShowPriceSavings } from '@/components/purchaseContextToggle/components/showPricingSavings';
import { DATA_KEYS } from '@/consts/dataKeys';
import { useRouter } from '@/hooks/useRouter';
import { type ResubscriptionCheckoutStep } from '@/services/funnelService/models/resubscriptionCheckoutStep';
import { PromotionService } from '@/services/promotionService/client';
import { ResubscriptionService } from '@/services/resubscriptionService/client';
import { AnalyticsEvent, trackEvent } from '@/utils/analytics';
import { useFlag } from '@/utils/featureFlags/client';

import {
  centsToDollarsAndCents,
  calculatePriceDifference,
  getPurchaseContextAwareData,
} from './utils/helpers';
import { type WinbackPromotion } from './utils/types';
import { useWinback } from './utils/useWinback';
import { useDataProvider } from '../../components/DataProvider/useDataProvider';
import { useFlowContext } from '../../hooks/useFlowContext';

export const ResubscriptionCheckout = ({
  step,
}: {
  step: ResubscriptionCheckoutStep;
}) => {
  const {
    subscribedProducts,
    patientData,
    paymentMethodData,
    productPriceMap,
    onetimeProductPriceMap,
    estimate,
    onetimeEstimate,
    previousSurveyResponses,
    previousFormulas,
    isEligibleToExpedite,
  } = useDataProvider(DATA_KEYS.resubscriptionCheckoutCombinedData);

  const router = useRouter();
  const { brand } = useFlowContext();
  const [isNavigating, setIsNavigating] = useState(false);
  const [patient, setPatient] = useState(patientData);

  const [paymentMethod, setPaymentMethod] = useState(paymentMethodData);
  const [promotion, setPromotion] = useState<WinbackPromotion>();

  const [showPaymentMethodModal, setShowPaymentMethodModal] = useState(false);
  const [showSubscriptionTermsModal, setShowSubscriptionTermsModal] =
    useState(false);
  const [showShippingAddressModal, setShowShippingAddressModal] =
    useState(false);
  const [showEditSubscriptionModal, setShowEditSubscriptionModal] =
    useState(false);
  const [flashMessages, setFlashMessages] = useAtom(
    FlashMessagesState.flashMessagesAtom,
  );

  useEffect(() => {
    const claimPromotion = async () => {
      const promo = await PromotionService.getWinbackPromotion(brand);
      setPromotion(promo.data.promotion);
    };

    claimPromotion();
  }, [brand]);

  const { shouldDisplayWinback, discountAmount } = useWinback(promotion);

  const onResubscribeClick = () => {
    if (isNavigating) {
      return;
    }

    setIsNavigating(true);
    trackEvent(AnalyticsEvent.RESUBSCRIBE_CLICKED, {
      purchaseContext: purchaseContext,
    });

    const resubscribeUser = async () => {
      await ResubscriptionService.resubscribeUser(
        brand,
        'skin',
        purchaseContext,
      );
    };

    resubscribeUser()
      .then(() => {
        router.refresh();
      })
      .catch(() => {
        setIsNavigating(false);
        console.error();
      });
  };

  const onShippingAddressUpdateSuccess = () => {
    setFlashMessages([
      ...flashMessages,
      {
        children: 'Shipping address updated successfully.',
        state: 'success',
      },
    ]);
    trackEvent(AnalyticsEvent.RESUBSCRIBE_INFORMATION_UPDATED, {
      moduleName: 'shipping',
    });
  };

  const { purchaseContext } = usePurchaseContext();

  const purchaseContextAwareEstimate = useMemo(
    () =>
      getPurchaseContextAwareData(purchaseContext, estimate, onetimeEstimate),
    [purchaseContext, estimate, onetimeEstimate],
  );

  const paymentInvoice = useMemo(
    () => createPaymentInvoice(brand, purchaseContextAwareEstimate),
    [brand, purchaseContextAwareEstimate],
  );

  const purchaseContextAwareProductPriceMap = getPurchaseContextAwareData(
    purchaseContext,
    productPriceMap,
    onetimeProductPriceMap,
  );
  const priceDifference = calculatePriceDifference(onetimeEstimate, estimate);
  const showPurchaseContextToggle = useFlag('curology-1-x-rx-in-resub');
  const showAlternateCopy = useFlag('curology-resubscription-alternate-copy');
  const showEditSubscriptionButton = useFlag(
    'curology-resubscription-edit-products',
  );

  const patientWantsPreviousFormula =
    previousSurveyResponses.previousResponses['continuePreviousFormula'];

  const imageUrl = step.contentfulImageUrl
    ? step.contentfulImageUrl
    : 'https://images.ctfassets.net/8pzh4g8cs30t/4Z7SpPucm1AzrMrWxpRqSp/5674b5c7dbc65c8a37caeb2da5077701/Frame_1197135680.png';

  return (
    <section className="cur-text-primary">
      <div className="cur-px-4 cur-py-8 cur-mx-auto">
        <div className="cur-max-w-sm cur-my-0 cur-mx-auto cur-relative">
          <div className="cur-text-[24px]	cur-font-bold cur-pb-2">
            {showAlternateCopy ? 'Checkout' : 'Review subscription'}
          </div>
          <Typography>
            {showAlternateCopy && patientWantsPreviousFormula === 'no'
              ? "Let's get your new formula in the works! Confirm your info, and then your provider will review your profile to prescribe the active ingredients for your skin and goals."
              : 'Confirm your information below.'}
          </Typography>
          <Image
            priority
            src={imageUrl}
            alt={'Hero image'}
            quality={100}
            width={375}
            height={275}
            className="cur-py-3 cur-w-[100%]"
          />
          {showPurchaseContextToggle && (
            <>
              <PurchaseContextToggle />
              <AnimatedPurchaseContextVisibility
                purchaseContexts={['subscription']}
                timeout={500}
                delay={200}
              >
                <ShowPriceSavings
                  purchaseContext={purchaseContext}
                  priceDifference={priceDifference.difference}
                />
              </AnimatedPurchaseContextVisibility>
            </>
          )}
          <div
            role="presentation"
            className="cur-bg-surface-neutral cur-text-[#1D1D44]"
          >
            {showPurchaseContextToggle &&
              subscribedProducts.map((product) => {
                return (
                  <ProductCard
                    key={product.id}
                    product={product}
                    productPriceMap={purchaseContextAwareProductPriceMap}
                    formulationData={
                      patientWantsPreviousFormula === 'yes'
                        ? previousFormulas.find(
                            (formulation) =>
                              formulation.productName === product.name,
                          )
                        : undefined
                    }
                    showEditIcon={false}
                  />
                );
              })}
            <InteractiveList>
              <InteractiveListItem
                onClick={() => setShowShippingAddressModal(true)}
                title="1.  Shipping address"
                className="data-hj-suppress"
              >
                {patient.address1}
                {` ${patient.address2}`}
              </InteractiveListItem>
              <InteractiveListItem
                onClick={() => setShowPaymentMethodModal(true)}
                title="2.  Payment method"
              >
                <PaymentMethodSummary paymentMethod={paymentMethod} />
              </InteractiveListItem>
            </InteractiveList>
            {purchaseContext !== 'onetime' && (
              <div className="cur-m-4">
                <Typography weight="bold">3. Review subscription</Typography>
                <div className="cur-flex cur-justify-between cur-my-2 cur-w-full">
                  <Typography>Your custom box</Typography>
                  <Typography>
                    {purchaseContextAwareEstimate.finalAmount.amount !==
                    purchaseContextAwareEstimate.originalAmount.amount ? (
                      <>
                        <span className="cur-font-bold cur-mr-2">
                          {centsToDollarsAndCents(
                            purchaseContextAwareEstimate.finalAmount.amount,
                          )}
                        </span>
                        <span className="cur-font-normal cur-line-through cur-text-disabled">
                          {centsToDollarsAndCents(
                            purchaseContextAwareEstimate.originalAmount.amount,
                          )}
                        </span>
                      </>
                    ) : (
                      <span className="cur-font-bold">
                        {centsToDollarsAndCents(
                          purchaseContextAwareEstimate.total.amount,
                        )}
                      </span>
                    )}
                  </Typography>
                </div>
                {subscribedProducts.map((product) => {
                  return (
                    <div className="cur-flex cur-mb-2" key={product.id}>
                      <CheckmarkIcon
                        size="sm"
                        className="cur-text-curology-brand"
                      />
                      <Typography size="meta" className="cur-pl-1">
                        {product.name}
                        {product.isRx ? <sup>Rx</sup> : null}
                      </Typography>
                    </div>
                  );
                })}
                <div className="cur-flex cur-mb-2">
                  <CheckmarkIcon
                    size="sm"
                    className="cur-text-curology-brand"
                  />
                  <Typography size="meta" className="cur-pl-1">
                    Free shipping
                  </Typography>
                </div>
                {shouldDisplayWinback && (
                  <div className="cur-flex cur-mb-2">
                    <CheckmarkIcon
                      size="sm"
                      className="cur-text-curology-brand"
                    />
                    <Typography size="meta" className="cur-pl-1">
                      Get{' '}
                      <span className="cur-text-success">
                        ${discountAmount} OFF
                      </span>{' '}
                      your first box
                    </Typography>
                  </div>
                )}
                {showEditSubscriptionButton && (
                  <Button
                    buttonType="text"
                    className="cur-pl-0"
                    onClick={() => setShowEditSubscriptionModal(true)}
                  >
                    <div className="cur-flex">
                      <EditIcon />
                      <Typography className="cur-pl-1" weight="bold">
                        Edit your box
                      </Typography>
                    </div>
                  </Button>
                )}
                <div onClick={() => setShowSubscriptionTermsModal(true)}>
                  <Typography size="meta" className="cur-py-4">
                    After your new consultation, you’ll automatically get a box
                    every 60 days at{' '}
                    {centsToDollarsAndCents(
                      purchaseContextAwareEstimate.originalAmount.amount,
                    )}{' '}
                    (plus tax where applicable). It’s that easy (and you can
                    cancel anytime). <u>See terms</u>
                  </Typography>
                </div>
              </div>
            )}
            <PaymentMethodModal
              paymentInvoice={paymentInvoice}
              setPaymentMethod={setPaymentMethod}
              setShowModal={setShowPaymentMethodModal}
              showModal={showPaymentMethodModal}
            />
            <SubscriptionTermsModal
              setShowModal={setShowSubscriptionTermsModal}
              showModal={showSubscriptionTermsModal}
            />
            <ShippingAddressModal
              setShowModal={setShowShippingAddressModal}
              showModal={showShippingAddressModal}
              user={patient}
              setPatient={setPatient}
              onSuccess={() => onShippingAddressUpdateSuccess()}
            />
            <EditSubscriptionModal
              estimate={estimate}
              subscribedProducts={subscribedProducts}
              purchaseContextAwareProductPriceMap={
                purchaseContextAwareProductPriceMap
              }
              setShowModal={setShowEditSubscriptionModal}
              showModal={showEditSubscriptionModal}
            />
          </div>
          {(!isEligibleToExpedite || patientWantsPreviousFormula === 'no') && (
            <div className="cur-bg-surface-highlight cur-mt-4 cur-p-4 cur-leading-5">
              Your provider will reach out about your formula within 2-3
              business days. You won&apos;t be charged until we process your
              order after that.
            </div>
          )}
          <Button
            className="cur-my-6 cur-w-full"
            buttonType="primary"
            onClick={onResubscribeClick}
            loading={isNavigating}
            disabled={!paymentMethod || isNavigating}
          >
            {!paymentMethod ? 'Add payment method' : step.nextBtnText}
          </Button>
          {purchaseContext !== 'onetime' && (
            <div className="cur-flex cur-justify-center cur-items-center cur-mt-4">
              <FormulaChangeIcon size="sm" />
              <span className="cur-text-[14px] cur-pl-3">
                {step.editYourBoxText}
              </span>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
